import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import toast from 'react-hot-toast';
import { GuardData } from '../../manage_guard/service/guardService';
import { getGuardSummary, GuardSummary } from '../service/paymentService';
import { getMonthName } from '../service/attendanceService';
import { FiTrendingUp, FiX, FiPlus, FiClock } from 'react-icons/fi';

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const GuardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[4]};
`;

const GuardImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
`;

const GuardDetails = styled.div`
  flex: 1;
`;

const GuardName = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const GuardMeta = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const SummaryCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SummaryValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const SummaryLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.white};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[700]} 100%);
  color: ${theme.colors.white};
`;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableBody = styled.tbody`
  & tr {
    border-bottom: 1px solid ${theme.colors.light.border.light};
    
    &:hover {
      background: ${theme.colors.light.background.secondary};
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const Badge = styled.span<{ variant?: 'success' | 'danger' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'info' }) => {
    if (variant === 'success') return `${theme.colors.accent.success}20`;
    if (variant === 'danger') return `${theme.colors.accent.danger}20`;
    if (variant === 'warning') return `${theme.colors.accent.warning}20`;
    return `${theme.colors.primary[500]}20`;
  }};
  color: ${({ variant = 'info' }) => {
    if (variant === 'success') return theme.colors.accent.success;
    if (variant === 'danger') return theme.colors.accent.danger;
    if (variant === 'warning') return theme.colors.accent.warning;
    return theme.colors.primary[600];
  }};
`;

interface GuardSalarySummaryModalProps {
  guard: GuardData;
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

const GuardSalarySummaryModal: React.FC<GuardSalarySummaryModalProps> = ({
  guard,
  isOpen,
  onClose,
  companyId,
}) => {
  const [summary, setSummary] = useState<GuardSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && guard.id) {
      loadSummary();
    }
  }, [isOpen, guard.id, companyId]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const data = await getGuardSummary(guard.id, companyId);
      setSummary(data);
    } catch (error: any) {
      console.error('Error loading guard summary:', error);
      toast.error('Failed to load guard summary: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `Rs. ${amount.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Guard Salary Summary"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <GuardInfo>
        {guard.image ? (
          <GuardImage src={guard.image} alt={guard.name} />
        ) : (
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: theme.colors.light.background.tertiary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: theme.typography.fontSize['2xl'],
              color: theme.colors.light.text.muted,
            }}
          >
            {guard.name?.charAt(0) || '?'}
          </div>
        )}
        <GuardDetails>
          <GuardName>{guard.name || 'Unknown Guard'}</GuardName>
          <GuardMeta>
            {guard.guardId || 'No ID'} {guard.cnic ? `• ${guard.cnic}` : ''}
          </GuardMeta>
        </GuardDetails>
      </GuardInfo>

      {loading ? (
        <LoadingContainer>
          <Spinner />
          <p style={{ color: theme.colors.light.text.secondary }}>Loading summary...</p>
        </LoadingContainer>
      ) : summary ? (
        <>
          <SummaryCards>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.success}>
                {formatCurrency(summary.totalPaidSalaryTillNow)}
              </SummaryValue>
              <SummaryLabel>Total Paid Salary</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.warning}>
                {formatCurrency(summary.totalAdvanceTaken)}
              </SummaryValue>
              <SummaryLabel>Total Advance Taken</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.danger}>
                {formatCurrency(summary.totalDeductions)}
              </SummaryValue>
              <SummaryLabel>Total Deductions</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.primary[600]}>
                {summary.totalPresents}
              </SummaryValue>
              <SummaryLabel>Total Presents</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.danger}>
                {summary.totalAbsents}
              </SummaryValue>
              <SummaryLabel>Total Absents</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.warning}>
                {summary.totalLeaves}
              </SummaryValue>
              <SummaryLabel>Total Leaves</SummaryLabel>
            </SummaryCard>
          </SummaryCards>

          {summary.deductions.length > 0 && (
            <Section>
              <SectionTitle>
                <FiX size={20} />
                Deductions History
              </SectionTitle>
              <TableWrapper>
                <StyledTable>
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>Month & Year</TableHeaderCell>
                      <TableHeaderCell>Amount</TableHeaderCell>
                      <TableHeaderCell>Reason</TableHeaderCell>
                      <TableHeaderCell>Paid At</TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {summary.deductions.map((deduction, index) => (
                      <tr key={index}>
                        <TableCell>
                          <Badge variant="info">
                            {getMonthName(deduction.monthNumber)} {deduction.year}
                          </Badge>
                        </TableCell>
                        <TableCell style={{ color: theme.colors.accent.danger, fontWeight: theme.typography.fontWeight.medium }}>
                          {formatCurrency(deduction.amount)}
                        </TableCell>
                        <TableCell>{deduction.reason || '—'}</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[1] }}>
                            <FiClock size={14} />
                            {formatDate(deduction.paidAt)}
                          </div>
                        </TableCell>
                      </tr>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableWrapper>
            </Section>
          )}

          {summary.payments.length > 0 && (
            <Section>
              <SectionTitle>
                <FiTrendingUp size={20} />
                Payment History
              </SectionTitle>
              <TableWrapper>
                <StyledTable>
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>Month & Year</TableHeaderCell>
                      <TableHeaderCell>Amount Paid</TableHeaderCell>
                      <TableHeaderCell>Paid At</TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {summary.payments.map((payment, index) => (
                      <tr key={index}>
                        <TableCell>
                          <Badge variant="success">
                            {getMonthName(payment.monthNumber)} {payment.year}
                          </Badge>
                        </TableCell>
                        <TableCell style={{ color: theme.colors.accent.success, fontWeight: theme.typography.fontWeight.medium }}>
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[1] }}>
                            <FiClock size={14} />
                            {formatDate(payment.paidAt)}
                          </div>
                        </TableCell>
                      </tr>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableWrapper>
            </Section>
          )}

          {summary.deductions.length === 0 && summary.payments.length === 0 && (
            <EmptyState>
              <FiTrendingUp size={48} style={{ marginBottom: theme.spacing[4], opacity: 0.5 }} />
              <p style={{ fontSize: theme.typography.fontSize.lg, margin: 0 }}>
                No payment or deduction history available yet.
              </p>
            </EmptyState>
          )}
        </>
      ) : (
        <EmptyState>
          <FiTrendingUp size={48} style={{ marginBottom: theme.spacing[4], opacity: 0.5 }} />
          <p style={{ fontSize: theme.typography.fontSize.lg, margin: 0 }}>
            No summary data available for this guard.
          </p>
        </EmptyState>
      )}
    </Modal>
  );
};

export default GuardSalarySummaryModal;

